import { action, atom, reaction, sleep } from '@reatom/framework'
import i18next from 'i18next'

import { Data, Result } from '@blade/std-lib'
import { refreshProfileAction } from '@root/entities/profile'
import { toSharedErrorCode } from '@root/features/secure-storage/secure-storage-error'
import { changeInviterRequestAction } from '@root/features/user/change-inviter'
import { ApiError } from '@root/shared/api'
import { UnknownError } from '@root/shared/core/Error/Error'
import { reatomRequest } from '@root/shared/lib'
import {
  showErrorCodeSnackbarAction,
  showErrorSnackbarAction,
} from '@root/shared/ui/error-snackbar'
import { showSnackbarAction } from '@root/shared/ui-kit'

import { reatomGetInviter } from './reatom-get-inviter'

import type { InviteCode } from '@root/entities/referral'

class ExistingShowCode extends Data.TaggedClass('ExistingShowCode')<{
  inviteCode: InviteCode | undefined
}> {}

class NewShowCode extends Data.TaggedClass('NewShowCode')<{
  inviteCode: InviteCode | undefined
}> {}

export type ShowCode = ExistingShowCode | NewShowCode

export const reatomUseInviteCodeForm = ({
  initialInviteCode,
  existingInviteCode,
}: {
  initialInviteCode: InviteCode | undefined
  existingInviteCode?: InviteCode | undefined
}) => {
  const inviteCodeAtom = atom<InviteCode | undefined>(
    initialInviteCode,
    'inviteCodeAtom',
  )
  const showCodeAtom = atom<ShowCode | undefined>(
    new ExistingShowCode({ inviteCode: existingInviteCode }),
    'showCodeAtom',
  )

  const { getInviterState, getInviter } = reatomGetInviter()

  // eslint-disable-next-line @reatom/unit-naming-rule
  const formRequestAtom = reatomRequest({
    execAction: changeInviterRequestAction,
    onResult: (ctx, result) => {
      if (Result.isSuccess(result)) {
        refreshProfileAction(ctx)
      }

      Result.match(result, {
        onFailure: (error) =>
          error instanceof UnknownError || ApiError.is(error)
            ? showErrorSnackbarAction(ctx, error)
            : showErrorCodeSnackbarAction(ctx, toSharedErrorCode(error)),
        onSuccess: () =>
          showSnackbarAction(ctx, {
            intent: 'success',
            text: i18next.t('referral.referral_code_has_been_successfully_set'),
          }),
      })
    },
  })

  const initReaction = reaction(async (ctx) => {
    const inviteCode = ctx.spy(inviteCodeAtom)
    await ctx.schedule(() => sleep(500))
    if (inviteCode) {
      const result = await ctx.schedule(() => getInviter(ctx, { inviteCode }))
      if (Result.isSuccess(result)) {
        showCodeAtom(ctx, new NewShowCode({ inviteCode }))
      }
      return
    }

    if (existingInviteCode) {
      const result = await ctx.schedule(() =>
        getInviter(ctx, { inviteCode: existingInviteCode }),
      )
      if (Result.isSuccess(result)) {
        showCodeAtom(
          ctx,
          new ExistingShowCode({ inviteCode: existingInviteCode }),
        )
      }
    }
  }, 'initReaction')

  const initAction = action((ctx) => {
    return initReaction(ctx)
  }, 'initAction')

  return {
    initAction,
    inviteCodeAtom,
    formRequestAtom,
    submitFormAction: formRequestAtom.execute,
    getInviterState,
    showCodeAtom,
  }
}

export interface UserInviteCodeFormModel
  extends ReturnType<typeof reatomUseInviteCodeForm> {}

export type UserInviteCodeFormInviterState =
  UserInviteCodeFormModel['getInviterState']
